// "Save place" bookmark toggle for a place page. Anonymous users see the
// button but get bounced to /login on click rather than having it hidden —
// same browse-everything philosophy as LoginGate, just inline.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bookmark } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  CREATE_SAVED_PLACE,
  DELETE_SAVED_PLACE,
  MY_SAVED_FOR_PLACE,
  type MySavedForPlaceResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";

export function SavePlaceButton({ placeId }: { placeId: string }) {
  const { personId } = useAuth();
  const { t } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ["saved-for-place", personId, placeId],
    // RLS makes this pointless when logged out — it would always be empty.
    enabled: personId != null,
    queryFn: () => gqlRequest<MySavedForPlaceResult>(MY_SAVED_FOR_PLACE, { placeId }),
  });
  const saved = data?.savedPlaces.nodes[0] ?? null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["saved-for-place", personId, placeId] });
    queryClient.invalidateQueries({ queryKey: ["saved", personId] });
  };

  const save = useMutation({
    mutationFn: () => gqlRequest(CREATE_SAVED_PLACE, { input: { personId, placeId } }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => gqlRequest(DELETE_SAVED_PLACE, { id }),
    onSuccess: invalidate,
  });

  const busy = (personId != null && isPending) || save.isPending || remove.isPending;

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={() => {
        if (!personId) {
          navigate(`/login?next=${encodeURIComponent(`/place/${placeId}`)}`);
          return;
        }
        if (saved) remove.mutate(saved.id);
        else save.mutate();
      }}
      disabled={busy}
      title={saved ? t("savePlace.savedTitle") : t("savePlace.buttonTitle")}
      className={
        saved
          ? "border-alpine-200 bg-alpine-50 text-alpine-700 hover:border-alpine-300 hover:bg-alpine-100 dark:border-alpine-900 dark:bg-alpine-950 dark:text-alpine-300 dark:hover:border-alpine-800 dark:hover:bg-alpine-900"
          : undefined
      }
    >
      <Bookmark aria-hidden className="size-4" fill={saved ? "currentColor" : "none"} />
      {saved ? t("savePlace.saved") : t("savePlace.button")}
    </Button>
  );
}
